"use client";

import { apiCommentGuess, type CommentGuessRequest, type UserBoughtEventGuessItem } from "@/api/comments";
import { toast } from "@/components/ui/showToast";
import { wrapContentWithReplyMarker } from "@/utils/comment-reply";
import { get as safeGet } from "@/utils/utils";
import { create } from "zustand";
import type { CommentData } from "./types";

type ReplyPagination = Record<number, { offsetId: number | null; hasMore: boolean }>;

interface CommentsState {
    selectedCommentId: string | null;
    replyDialogOpen: boolean;
    targetComment: CommentData | null;
    replyParentId: string | null;
    eventNo: string;
    commentId: string;
    postingComment: boolean;
    replying: boolean;
    replyPagination: ReplyPagination;
    userGuessOptions: UserBoughtEventGuessItem[];
    guessTitleMap: Record<string, string>;
    submitComment: ( content: string, option?: UserBoughtEventGuessItem | null ) => Promise<void>;
    submitReply: ( content: string ) => Promise<void>;
    toggleSelect: ( commentId: string ) => void;
    openReplyDialog: ( comment: CommentData, parentId: string ) => void;
    closeReplyDialog: () => void;
    setUserGuessOptions: ( options: UserBoughtEventGuessItem[] ) => void;
    enrichUserGuessOptionsWithTitles: ( guessList: Array<{ guessNo: string; title: string }> ) => void;
}

const applyGuessTitles = (
    options: UserBoughtEventGuessItem[],
    guessTitleMap: Record<string, string>,
): UserBoughtEventGuessItem[] => {
    if ( !options || options.length === 0 ) {
        return [];
    }
    return options.map( ( option ) => ( {
        ...option,
        guessTitle: guessTitleMap[option.guessNo] ?? option.guessTitle,
    } ) );
};

const getDefaultGuessOption = ( options: UserBoughtEventGuessItem[] ) => options[0];

export const useCommentsStore = create<CommentsState>( ( set, get ) => ( {
    selectedCommentId: null,
    replyDialogOpen: false,
    targetComment: null,
    replyParentId: null,
    eventNo: "",
    commentId: "",
    postingComment: false,
    replying: false,
    replyPagination: {},
    userGuessOptions: [],
    guessTitleMap: {},
    async submitComment( content, selectedOption ) {
        const trimmed = content.trim();
        if ( !trimmed ) return;
        const { eventNo, userGuessOptions } = get();
        if ( !eventNo ) return;
        set( { postingComment: true } );
        try {
            const guessOption = selectedOption ?? getDefaultGuessOption( userGuessOptions );
            const payload: CommentGuessRequest = {
                content: trimmed,
                eventNo,
            };
            if ( guessOption ) {
                payload.guessNo = guessOption.guessNo;
                payload.betType = guessOption.betType;
            }
            const [ isOk, res ] = await apiCommentGuess( payload );
            if ( !isOk || safeGet( res, "code", 0 ) !== 0 ) {
                toast.error( safeGet( res, "msg", "Failed To Submit Comment!" ) );
                return;
            }
            // Note: Comment list refresh is handled by React Query invalidation in useCommentInput hook
        } catch ( error ) {
            console.error( "[comments] Failed to submit comment", error );
            toast.error( "Failed To Submit Comment!" );
        } finally {
            set( { postingComment: false } );
        }
    },
    async submitReply( content ) {
        const trimmed = content.trim();
        const { targetComment, eventNo } = get();
        if ( !trimmed || !targetComment || !eventNo ) return;
        set( { replying: true } );
        try {
            const shouldAttachMarker = Boolean( targetComment.parentServerId );
            const payload: CommentGuessRequest = {
                content: shouldAttachMarker
                    ? wrapContentWithReplyMarker( trimmed, targetComment.serverId )
                    : trimmed,
                parentId: targetComment.serverId,
                eventNo,
            };
            const [ isOk, res ] = await apiCommentGuess( payload );
            if ( !isOk || safeGet( res, "code", 0 ) !== 0 ) {
                toast.error( safeGet( res, "msg", "Failed To Submit Reply!" ) );
                return;
            }
            // Note: Comment list refresh is handled by React Query invalidation in useCommentsTab hook
        } catch ( error ) {
            console.error( "[comments] Failed to submit reply", error );
            toast.error( "Failed To Submit Reply!" );
        } finally {
            set( { replying: false } );
        }
    },
    toggleSelect: ( commentId ) =>
        set( ( state ) => ( {
            selectedCommentId: state.selectedCommentId === commentId ? null : commentId,
        } ) ),
    openReplyDialog: ( comment, parentId ) =>
        set( {
            replyDialogOpen: true,
            targetComment: comment,
            replyParentId: parentId,
        } ),
    closeReplyDialog: () =>
        set( {
            replyDialogOpen: false,
            targetComment: null,
            replyParentId: null,
        } ),
    setUserGuessOptions: ( options ) =>
        set( ( state ) => ( {
            userGuessOptions: applyGuessTitles( options, state.guessTitleMap ),
        } ) ),
    enrichUserGuessOptionsWithTitles: ( guessList ) => {
        set( ( state ) => {
            const nextGuessTitleMap: Record<string, string> = { ...state.guessTitleMap };
            guessList.forEach( ( guess ) => {
                if ( guess.title ) {
                    nextGuessTitleMap[guess.guessNo] = guess.title;
                }
            } );

            return {
                guessTitleMap: nextGuessTitleMap,
                userGuessOptions: applyGuessTitles( state.userGuessOptions, nextGuessTitleMap ),
            };
        } );
    },
} ) );
